import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import type { Preset } from '@/constants/presets';
import { scrollToSection } from '@/utils/scrollToSection';

import { templateSignupHref, useTemplateTranslation } from './kit';

const MAX_FIELD_CHIPS = 4;

/** Three honest steps: what the visitor actually does on this page. */
export default function TemplateHowItWorks({ preset }: { preset: Preset }) {
  const { t, lang } = useTemplateTranslation();
  const title = t(preset.title) || preset.title;

  const labels = (preset.fields || [])
    .filter((field) => field.type !== 'hidden' && !field.imageReferenceOnly)
    .map((field) => t(field.label));
  const shown = labels.slice(0, MAX_FIELD_CHIPS);
  const more = labels.length - shown.length;

  const fieldChips = shown.length ? (
    <ul className='mt-5 flex flex-wrap gap-1.5'>
      {shown.map((label) => (
        <li
          key={label}
          className={clsxm(
            'max-w-full truncate rounded-full bg-[#f3f3f9] px-2.5 py-1 text-xs font-medium dark:bg-white/[0.06]',
            tone.body,
          )}
        >
          {label}
        </li>
      ))}
      {more > 0 ? (
        <li className={clsxm('px-1 py-1 text-xs font-medium', tone.quiet)}>
          {t('+{{count}} more', { count: more })}
        </li>
      ) : null}
    </ul>
  ) : null;

  const steps = preset.isFree
    ? [
        {
          title: t('Fill in a few details'),
          body: t('Tell the tool what you need. Short answers work fine.'),
          extra: fieldChips,
        },
        {
          title: t('Click Generate'),
          body: t('Your result appears in seconds, right next to your inputs.'),
        },
        {
          title: t('Copy, tweak, repeat'),
          body: t(
            'Copy what you like, or change your inputs and try again. Sign up free to save your results.',
          ),
        },
      ]
    : [
        {
          title: t('Create a free account'),
          body: t('It takes a few seconds. No credit card needed.'),
        },
        {
          title: t('Fill in a few details'),
          body: t('Tell the tool what you need. Short answers work fine.'),
          extra: fieldChips,
        },
        {
          title: t('Generate and edit'),
          body: t('Click Generate, then copy, edit or save the result.'),
        },
      ];

  return (
    <section
      id='how-it-works'
      className='mx-auto max-w-6xl px-4 pt-24 sm:px-6 sm:pt-32 lg:px-8'
    >
      <div className='flex flex-wrap items-end justify-between gap-x-10 gap-y-6'>
        <SectionHeading
          size='md'
          title={
            lang === 'en'
              ? `How to use the ${title}`
              : t("It's easy to get started")
          }
        />
        {preset.isFree ? (
          <a
            href='#tool'
            onClick={(event) => scrollToSection(event, 'tool')}
            className={buttonSecondary}
          >
            {t('Try it now')}
            <ArrowGlyph className='-rotate-90 rtl:rotate-90' />
          </a>
        ) : (
          <a href={templateSignupHref(preset)} className={buttonSecondary}>
            {preset.onlyForPaidPlans
              ? t('Get started')
              : t('Get started for free')}
            <ArrowGlyph />
          </a>
        )}
      </div>

      <ol className='mt-12 grid gap-px overflow-hidden rounded-[24px] bg-[#e7e7ef] ring-1 ring-[#0f0f28]/[0.07] md:grid-cols-3 dark:bg-[#26253a] dark:ring-white/[0.08]'>
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={clsxm('flex flex-col p-7 sm:p-8', tone.surface)}
          >
            <span className='text-[13px] font-semibold tabular-nums text-primary-600 dark:text-primary-300'>
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3
              className={clsxm(
                'mt-5 text-lg font-semibold leading-7',
                tone.ink,
              )}
            >
              {step.title}
            </h3>
            <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
              {step.body}
            </p>
            {step.extra}
          </li>
        ))}
      </ol>
    </section>
  );
}
